import { Badge, Button, Card, CardHeader, EmptyState, Table, formatDate, formatTime, useToast, type Column } from "@helmr/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { newIdempotencyKey } from "../../api/client";
import { myMonth, selfPunch, type AttendanceDay, type Punch } from "../../api/attendance";
import { useServices } from "../../app/services";
import { Failure, FormError, messageOf, RowsSkeleton, styles as c, useScope } from "../classes/common";
import { cellKind, STATUS_LABEL } from "./attendanceBits";
import { monthLabel } from "./month";

/** Today's punches and one Punch button. Geo and device rules, if the gym set any, are the API's: its refusal is shown as it is. */
export function MyAttendance() {
  const { client } = useServices();
  const sc = useScope();
  const qc = useQueryClient();
  const toast = useToast();
  const key = useRef(newIdempotencyKey());
  const [error, setError] = useState<string | null>(null);
  const month = sc.date.slice(0, 7);
  const q = useQuery({ queryKey: ["attendance", ...sc.key, "me", month], queryFn: () => myMonth(client, month), retry: false });
  const punch = useMutation({
    mutationFn: () => selfPunch(client, key.current),
    onSuccess: (r) => { key.current = newIdempotencyKey(); setError(null); void qc.invalidateQueries({ queryKey: ["attendance"] }); toast.show(r.collapsed ? "You punched a moment ago" : "Punched"); },
    onError: (e) => setError(messageOf(e)),
  });
  const t = (ms: number | null) => (ms === null ? "—" : formatTime(ms, sc.ctx.timeZone));
  const todays: Punch[] = (q.data?.punches ?? []).filter((p) => p.businessDate === sc.date);
  const dayCols: Column<AttendanceDay>[] = [
    { key: "d", header: "Date", cell: (d) => formatDate(d.date, sc.ctx.locale, "weekday") },
    { key: "s", header: "Status", cell: (d) => STATUS_LABEL[cellKind(d)] ?? d.status },
    { key: "i", header: "In", cell: (d) => t(d.firstIn) },
    { key: "o", header: "Out", cell: (d) => (d.open ? "Still in" : t(d.lastOut)) },
  ];
  return (
    <div className={c.page}>
      <div className={c.head}><div><h1>My attendance</h1><p>{monthLabel(month, sc.ctx.locale)}</p></div></div>
      <Card aria-label="Today">
        <CardHeader title="Today" action={<Button variant="primary" busy={punch.isPending} busyLabel="Punching…" onClick={() => punch.mutate()}>Punch</Button>} />
        <div className={`${c.pad} ${c.stack}`}>
          <FormError message={error} />
          {q.isPending ? <RowsSkeleton rows={2} /> : q.error ? <Failure error={q.error} title="Could not load your attendance" onRetry={() => void q.refetch()} /> : todays.length === 0 ? (
            <EmptyState compact icon="clock" title="No punches today" description="Press Punch when you arrive." />
          ) : (
            <ul aria-label="Today's punches" className={c.list}>{todays.map((p) => <li key={p.id} className={c.li}><div><strong>{p.direction === "in" ? "In" : "Out"} · {formatTime(p.at, sc.ctx.timeZone)}</strong><span className={c.sub}>{p.source}</span></div>{p.source === "manual" ? <Badge tone="warn">Added by a manager</Badge> : null}</li>)}</ul>
          )}
        </div>
      </Card>
      {q.data && q.data.days.length > 0 ? <Card aria-label="This month"><CardHeader title="This month" /><Table label="Days" columns={dayCols} rows={q.data.days} rowKey={(d) => d.date} minWidth={420} /></Card> : null}
    </div>
  );
}
